import { Controller, FormProvider } from 'react-hook-form';
import { StanButton, StanInput, StanPhoneInput } from '@/components';
import { PhoneChange } from '@/components/core-components/StanPhoneInput/StanPhoneInput.types';
import { useRegisterForm } from '../hooks/useRegisterForm';
import { useUsernameAvailability } from '../hooks/useUsernameAvailability';
import { getUsernameHint, hasUsernameError } from '../logic/username.logic';
import { UsernameInput } from './UsernameInput';
import IconPerson from '@/assets/icons/ic-profile.svg';
import IconMail from '@/assets/icons/ic-mail.svg';
import IconLock from '@/assets/icons/ic-lock.svg';

interface RegisterFormProps {
    onUsernameChange?: (username: string) => void;
    /** Override default country for phone input (ISO 3166-1 alpha-2 code) */
    defaultCountry?: string;
}

export const RegisterForm = ({ onUsernameChange, defaultCountry }: RegisterFormProps) => {
    const { form, onSubmit, isSubmitting } = useRegisterForm();
    const {
        control,
        formState: { errors, touchedFields },
        setValue,
        watch,
    } = form;

    // Watch username for availability check
    const username = watch('username');
    const { isChecking, isAvailable, error: availabilityError } = useUsernameAvailability(username);

    const handlePhoneChange = (value: PhoneChange) => {
        setValue('phoneNumber', value.e164, { shouldValidate: true });
        if (value.country) {
            setValue('countryCode', value.country, { shouldValidate: true });
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={onSubmit} className='space-y-2.5'>
                {/* Username Input with stan.store/ prefix */}
                <Controller
                    name='username'
                    control={control}
                    render={({ field }) => {
                        // Only show errors after the field has been blurred (touched)
                        const isTouched = !!touchedFields.username;
                        const showError = isTouched && hasUsernameError(!!errors.username, isAvailable);
                        const hintText = isTouched
                            ? getUsernameHint(errors.username?.message, availabilityError, isAvailable)
                            : undefined;

                        return (
                            <UsernameInput
                                id='username'
                                name={field.name}
                                value={field.value}
                                onChange={e => {
                                    // Only allow valid username characters
                                    const value = e.target.value.replace(/[^A-Za-z0-9-_]/g, '');
                                    field.onChange(value);
                                    onUsernameChange?.(value);
                                }}
                                onBlur={field.onBlur}
                                disabled={isSubmitting}
                                error={showError}
                                hintText={hintText}
                                placeholder='username'
                                isChecking={isChecking}
                                isAvailable={isAvailable}
                            />
                        );
                    }}
                />

                {/* Full Name Input */}
                <Controller
                    name='fullName'
                    control={control}
                    render={({ field }) => (
                        <StanInput
                            id='fullName'
                            name={field.name}
                            type='text'
                            placeholder='Full Name'
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={isSubmitting}
                            error={!!errors.fullName}
                            hintText={errors.fullName?.message}
                            startIcon={IconPerson}
                        />
                    )}
                />

                {/* Email Input */}
                <Controller
                    name='email'
                    control={control}
                    render={({ field }) => (
                        <StanInput
                            id='email'
                            name={field.name}
                            type='email'
                            placeholder='Email'
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={isSubmitting}
                            error={!!errors.email}
                            hintText={errors.email?.message}
                            startIcon={IconMail}
                        />
                    )}
                />

                {/* Phone Number Input */}
                <Controller
                    name='phoneNumber'
                    control={control}
                    render={({ field }) => (
                        <StanPhoneInput
                            id='phoneNumber'
                            name={field.name}
                            placeholder='Phone Number'
                            value={field.value}
                            onChange={handlePhoneChange}
                            error={!!errors.phoneNumber}
                            hintText={errors.phoneNumber?.message}
                            defaultCountry={defaultCountry}
                        />
                    )}
                />

                {/* Password Input */}
                <Controller
                    name='password'
                    control={control}
                    render={({ field }) => (
                        <StanInput
                            id='password'
                            name={field.name}
                            type='password'
                            placeholder='Password'
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={isSubmitting}
                            error={!!errors.password}
                            hintText={errors.password?.message}
                            startIcon={IconLock}
                        />
                    )}
                />

                {/* Submit Button */}
                <div className='pt-6 pb-9'>
                    <StanButton
                        type='submit'
                        name='register-next'
                        color='primary'
                        className='w-full'
                        disabled={isSubmitting || isChecking || isAvailable === false}
                    >
                        Next
                    </StanButton>
                </div>
            </form>
        </FormProvider>
    );
};
